// Zeiterfassung aller Mitarbeiter — nur für Admins.
//
// Der Mitarbeiter selbst sieht in /crm/zeit ausschließlich seine eigenen Zeiten.
// Hier steht die Gesamtsicht — und das, was die Leitung wirklich braucht:
// Urlaubsanträge entscheiden und den Stundenzettel für die Lohnbuchhaltung ziehen.

import { useMemo, useState } from "react";
import { useAlleZeiten, dauerMinuten, formatDauer, type TimeEntryMitPerson } from "@/lib/zeit";
import { useOffeneAntraege } from "@/lib/urlaub";
import { LogoLoader } from "@/components/LogoLoader";
import { Card, EmptyState, Segmented, cn } from "@/design-system/ui";
import { ModulHeader } from "@/modules/home/ModulHeader";
import { AntraegeTab } from "./AntraegeTab";
import { UrlaubsJahr } from "./UrlaubsJahr";
import { LohnTab } from "./LohnTab";
import { PausenregelKarte } from "./PausenregelKarte";

type Register = "zeiten" | "antraege" | "urlaubsjahr" | "lohn";
type Zeitraum = 7 | 30 | 90;

export default function AdminZeitenPage() {
  const [register, setRegister] = useState<Register>("zeiten");
  const { data: offen = [] } = useOffeneAntraege();

  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col">
      <ModulHeader titel="Zeiterfassung" />

      <div className="shrink-0 border-b border-ink-700/70 px-4 py-3">
        <Segmented
          value={register}
          onChange={setRegister}
          options={[
            { value: "zeiten", label: "Zeiten" },
            // Der Punkt am Register ist der Grund, warum Sebastian hier reinschaut:
            // Ein Antrag, den niemand sieht, ist ein Antrag, der liegen bleibt.
            { value: "antraege", label: offen.length ? `Anträge · ${offen.length}` : "Anträge" },
            // Neben den Anträgen, nicht darin: Das eine ist "entscheiden", das
            // andere "überblicken" — zwei verschiedene Fragen.
            { value: "urlaubsjahr", label: "Urlaubsjahr" },
            { value: "lohn", label: "Lohn" },
          ]}
        />
      </div>

      {register === "zeiten" ? (
        <ZeitenTab />
      ) : register === "antraege" ? (
        <AntraegeTab />
      ) : register === "urlaubsjahr" ? (
        <UrlaubsJahr />
      ) : (
        <LohnTab />
      )}
    </div>
  );
}

function ZeitenTab() {
  const [tage, setTage] = useState<Zeitraum>(30);

  const von = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - tage);
    return d.toISOString();
  }, [tage]);

  const { data: eintraege = [], isLoading } = useAlleZeiten(von);

  // Nach Person gruppieren, laufende Schichten separat ausweisen.
  const proPerson = useMemo(() => {
    const map = new Map<
      string,
      { name: string; rolle: string; summe: number; laeuft: boolean; zeilen: TimeEntryMitPerson[] }
    >();

    for (const e of eintraege) {
      const key = e.profile_id;
      const name = e.person?.full_name ?? "Ohne Namen";
      const g = map.get(key) ?? {
        name,
        rolle: e.person?.role ?? "",
        summe: 0,
        laeuft: false,
        zeilen: [],
      };
      // Nur abgeschlossene Schichten zählen — eine laufende hätte keine feste Dauer.
      if (e.ended_at) g.summe += dauerMinuten(e);
      else g.laeuft = true;
      g.zeilen.push(e);
      map.set(key, g);
    }

    return [...map.values()].sort((a, b) => b.summe - a.summe);
  }, [eintraege]);

  const gesamt = proPerson.reduce((s, p) => s + p.summe, 0);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
      <div className="mb-4">
        <PausenregelKarte />
      </div>

      <Segmented
        className="mb-4"
        value={tage}
        onChange={setTage}
        options={[
          { value: 7, label: "7 Tage" },
          { value: 30, label: "30 Tage" },
          { value: 90, label: "90 Tage" },
        ]}
      />

      {!isLoading && proPerson.length > 0 && (
        <p className="mb-3 text-sm tabular-nums text-ink-400">
          {proPerson.length} Mitarbeiter · {formatDauer(gesamt)}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-16">
          <LogoLoader size="md" label="Zeiten werden geladen" />
        </div>
      ) : proPerson.length === 0 ? (
        <EmptyState
          title="Keine Zeiten erfasst"
          hint={`In den letzten ${tage} Tagen hat niemand gestempelt.`}
        />
      ) : (
        <ul className="space-y-3">
          {proPerson.map((p) => (
            <li key={p.name}>
              <PersonKarte {...p} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PersonKarte({
  name,
  rolle,
  summe,
  laeuft,
  zeilen,
}: {
  name: string;
  rolle: string;
  summe: number;
  laeuft: boolean;
  zeilen: TimeEntryMitPerson[];
}) {
  const [offen, setOffen] = useState(false);
  const fertige = zeilen.filter((z) => z.ended_at);

  return (
    <Card className="overflow-hidden">
      <button
        onClick={() => setOffen((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors duration-fast hover:bg-ink-800"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-[15px] font-medium text-ink-100">{name}</span>
            {laeuft && (
              <span className="flex shrink-0 items-center gap-1 rounded bg-gruen-500/15 px-1.5 py-0.5 text-xs font-medium text-gruen-400">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-gruen-400" />
                aktiv
              </span>
            )}
          </div>
          <div className="mt-0.5 text-sm text-ink-400">
            {rolle} · {fertige.length} {fertige.length === 1 ? "Schicht" : "Schichten"}
          </div>
        </div>

        <div className="shrink-0 text-right">
          <div className="text-[15px] font-semibold tabular-nums text-ink-50">
            {formatDauer(summe)}
          </div>
          <div className="mt-0.5 text-xs text-ink-500">{offen ? "einklappen" : "Details"}</div>
        </div>
      </button>

      {offen && (
        <ul className="divide-y divide-ink-700/50 border-t border-ink-700/70">
          {zeilen.map((z) => (
            <li
              key={z.id}
              className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm"
            >
              <span className="tabular-nums text-ink-300">
                {new Date(z.started_at).toLocaleDateString("de-DE", {
                  weekday: "short",
                  day: "2-digit",
                  month: "2-digit",
                })}
                <span className="ml-2 text-ink-500">
                  {uhr(z.started_at)} – {z.ended_at ? uhr(z.ended_at) : "läuft"}
                </span>
              </span>
              <span
                className={cn(
                  "shrink-0 tabular-nums",
                  z.ended_at ? "text-ink-100" : "text-gruen-400"
                )}
              >
                {z.ended_at ? formatDauer(dauerMinuten(z)) : "…"}
                {z.break_minutes > 0 && (
                  <span className="ml-1.5 text-xs text-ink-500">−{z.break_minutes} min</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function uhr(iso: string) {
  return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}
